// src/hooks/useTpnCart.ts
import { useMemo } from 'react';
import { useAppSelector } from '../redux/hooks';
import { shouldShowCartIcon } from '../utils/tpnUtils';
import { checkIfRestaurantClosed } from '../utils/reservationUtils';

/**
 * Custom hook for determining cart icon visibility based on TPN configuration
 * @param showCartIcon - Whether cart icon is enabled in widget settings
 * @returns boolean indicating if cart icon should be shown
 */
export const useTpnCart = (showCartIcon: boolean): boolean => {
  // Get TPN configuration from Redux store
  const tpnConfig = useAppSelector((state) => state?.tpnConfig?.data);
  
  // Get selected restaurant from Redux store
  const selectedRestaurant = useAppSelector((state) => state?.restaurant?.selectedRestaurant);
  
  // Get table availability state from Redux store
  const { 
    noTablesAvailable, 
    loading: tableLoading, 
    time_slots 
  } = useAppSelector((state) => state.tableAvailability);
  
  // Get site content for restaurant hours
  const siteContent = useAppSelector((state) => state.siteContent);
  
  // Memoize the cart visibility logic with complete reservation checks
  const shouldShowCart = useMemo(() => {
    // Check 0: isAddToCartEnabled (HIGHEST PRIORITY - from backend)
    // Default to true if key doesn't exist (backward compatible)
    const isAddToCartEnabled = selectedRestaurant?.payment_perferences?.isAddToCartEnabled ?? true;
    if (!isAddToCartEnabled) return false;
    
    // Check 1: Restaurant must be open today
    const todayDate = new Date().toISOString().split('T')[0];
    const isRestaurantClosed = checkIfRestaurantClosed(todayDate, siteContent);
    if (isRestaurantClosed) return false;
    
    // Check 2: TPN Configuration must exist
    const hasTpnConfig = shouldShowCartIcon(showCartIcon, tpnConfig);
    if (!hasTpnConfig) return false;
    
    // Check 3: Don't show cart while loading table availability
    // if (tableLoading) return false;
    
    // Check 4: Time slots must exist (catches fully booked case)
    // Same logic as TableReservationsPage: timeSlots.length === 0
    if (!time_slots || time_slots.length === 0) return false;
    
    // Check 5: Tables must be available (safety check)
    if (noTablesAvailable) return false;
    
    // All checks passed - show cart
    return true;
  }, [selectedRestaurant, siteContent, showCartIcon, tpnConfig, tableLoading, time_slots, noTablesAvailable]);
  
  return shouldShowCart;
};

/**
 * Custom hook for getting TPN configuration status
 * @returns object with TPN status information
 */
export const useTpnStatus = () => {
  const tpnConfig = useAppSelector((state) => state?.tpnConfig?.data);
  
  return useMemo(() => ({
    isActive: !!(tpnConfig?.tpn_config && tpnConfig.tpn_config.length > 0),
    configCount: tpnConfig?.tpn_config?.length || 0,
    hasConfig: !!tpnConfig,
    config: tpnConfig
  }), [tpnConfig]);
};
